import { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCwIcon } from 'lucide-react';
import { RiArrowGoBackFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { ErrorCode, logFatal } from '../logger';
import CustomButton from '../../feature-module/components/CustomButton';

/** ---- Wrapper to inject navigate() into class component ---- */
export function withNavigation(ComponentClass: any) {
  return function WrappedComponent(props: any) {
    const navigate = useNavigate();
    return <ComponentClass {...props} navigate={navigate} />;
  };
}

interface Props {
  children: ReactNode;
  navigate?: (path: string, options?: { replace?: boolean }) => void; // added navigate
}

interface State {
  hasError: boolean;
  error?: Error;
  isStaleDeployment?: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    const message = error?.message || '';
    if (message.includes('Failed to fetch dynamically imported module')) {
      return { hasError: true, isStaleDeployment: true };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    // Stale deployment: a new build was deployed while this tab was open.
    // Old chunks were removed, so the dynamic import 404s. Reload fixes it.
    const message =
      error instanceof Error ? error.message : String(error ?? '');
    if (message.includes('Failed to fetch dynamically imported module')) {
      window.location.reload();
      return;
    }

    // Serialize non-Error throws (e.g. {}, undefined, strings) so logs capture useful detail
    const serializedError =
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : error != null && typeof error === 'object'
          ? {
              type: 'object',
              value: JSON.stringify(error, Object.getOwnPropertyNames(error)),
            }
          : { type: typeof error, value: String(error) };

    logFatal(
      'UI render crash',
      { errorStack: { error: serializedError, errorInfo } },
      ErrorCode.UI_RENDER_ERROR
    );
  }

  refreshPage = () => {
    window.location.reload();
  };

  navigateToDashboard = () => {
    this.setState({ hasError: false });
    this.props.navigate?.('/', { replace: true });
  };

  render() {
    if (this.state.isStaleDeployment) {
      // Stale deployment — reload already queued in componentDidCatch.
      // Render nothing to avoid flashing the "Oops!" UI.
      return null;
    }

    if (this.state.hasError) {
      // Handle 503 specially
      if ((this.state.error as Error)?.message?.includes('503')) {
        return (
          <section className="flex justify-center items-center h-[80vh]">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-red-600 mb-4">
                503 Service Unavailable
              </h1>
              <p className="text-lg text-gray-600">
                Our servers are down. Please try again later.
              </p>
            </div>
          </section>
        );
      }

      // Default error UI
      return (
        <section className="flex justify-center items-center h-[80vh]">
          <div className="py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6">
            <div className="mx-auto max-w-screen-sm text-center">
              <p className="mb-4 text-xl tracking-tight font-bold md:text-3xl text-red-500">
                Oops! Something went wrong.
              </p>
              <p className="mb-4 text-sm md:text-lg font-light text-gray-500">
                Please try refreshing the page or go back to the dashboard. If
                the issue persists, feel free to reach out to our support team.
              </p>

              <div className="justify-center gap-4 hidden md:flex">
                <CustomButton
                  label="Go to Dashboard"
                  onPress={this.navigateToDashboard}
                  variant="flat"
                  color="primary"
                  size="md"
                  className="text-sm font-medium"
                  startContent={<RiArrowGoBackFill className="w-4 h-4 mr-2" />}
                />

                <CustomButton
                  label=" Refresh Page"
                  onPress={this.refreshPage}
                  variant="faded"
                  color="primary"
                  size="md"
                  className="text-sm font-medium"
                  startContent={<RefreshCwIcon className="w-4 h-4 mr-2" />}
                />
              </div>
            </div>
          </div>
        </section>
      );
    }

    return this.props.children;
  }
}

export default withNavigation(ErrorBoundary);
